import { Handle, Position } from '@xyflow/react';
import classnames from 'classnames';
import { ResourceCard } from 'js/components/pipelines/cards/ResourceCard';
import { ActionMenu } from 'js/components/pipelines/cards/components/ActionMenu';
import { ActionItem } from 'js/components/pipelines/cards/components/ActionMenu/ActionMenu';
import {
  FLOW_TYPE_CUSTOM,
  PIPELINE_CARD_SPACING,
  PIPELINE_CARD_WIDTH,
} from 'js/constants/pipelineConstants';
import { getConnectionOrVendorType, isSqlAutomation } from 'js/lib/ResourceUtils';
import { CONNECTION_CATEGORY_FILE_UPLOAD } from 'js/constants/connectionCategories';
import { has, max, pick, values } from 'lodash';
import { AnimatePresence, motion } from 'motion/react';
import DataSetSplitDialog from 'new/components/SplitDataSet/DataSetSplitDialog';
import { useDraftContext } from 'new/components/flows/types/genai_rag/context/Draft/useDraftContext';
import useRagFlowId from 'new/components/flows/types/genai_rag/context/RagFlowId/useRagFlowId';
import { ModalConfigureReranker } from 'new/elements/flows/ModalConfigureReranker';
import React, { memo, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import DataSetCardContainer from '../../cards/DataSetCardContainer';
import DataSinkCardContainer from '../../cards/DataSinkCardContainer';
import DataSourceCardContainer from '../../cards/DataSourceCardContainer';
import { shouldRenderExpandActions } from '../../cards/components/ExpandActions/utils/shouldRenderSourceAction';
import RetrieverActions from '../../cards/components/RetrieverActions';
import { NodeBox } from '../../cards/components/NodeBox';
import { ResourceName } from '../../cards/components/ResourceName';
import { ReactComponent as SplitIcon } from 'new/components/icons/fi-sr-split.svg';
import classes from '../FlowNodes.module.scss';

const styles = {
  main: {
    whiteSpace: 'nowrap',
  },
  children: {
    display: 'inline-block',
    verticalAlign: 'top',
    paddingLeft: 20,
  },
  iconContainer: {
    float: 'left',
    height: 80,
    padding: '0 10px',
  },
};

function ItemCard({
  isPreview,
  isFlowRunPreview,
  isDataFlowStatus,
  item,
  aclQueryMode,
  onClickOrchestrate,
  expanded,
  size,
  node_type,
  onItemClick,
  onAddReranker,
  toggleUseExpanded,
  splitterNodeProps,
  isNodeDiscoveryEnabled,
  canvasScheduleControlsActive,
  isMultifileUploadEnabledOnSource,
}) {
  const { isEdit, draftState } = useDraftContext();
  const ragFlowId = useRagFlowId();
  const ragLinkedFlowIds = useSelector(
    (state: any) => state.entities.flowNodes[ragFlowId]?.linked_flows,
  );

  // Create a memoized selector for ragLinkedFlows
  const getRagLinkedFlowsSelector = useMemo(() => {
    return createSelector(
      [(state: any) => state.entities.flowNodes, () => ragLinkedFlowIds],
      (flowNodes, linkedFlowIds) => (linkedFlowIds ? values(pick(flowNodes, linkedFlowIds)) : []),
    );
  }, [ragLinkedFlowIds]);

  const ragLinkedFlows = useSelector(getRagLinkedFlowsSelector);

  const isRetrieverDataSet =
    item.type === 'DATA_SET' &&
    node_type === 'data_retriever_holder' &&
    (ragLinkedFlows.some(({ data_retriever_ids }) => data_retriever_ids?.includes(item.id)) ||
      draftState.dataSetsPendingLinking?.includes(item.id));

  const cardProps = {
    cardData: item,
    data: item,
    onClickOrchestrate,
    size,
    expanded,
    isPreview,
    hideActions:
      node_type === 'data_retriever_holder' &&
      item.type !== 'SOURCE' &&
      (item.type !== 'DATA_SET' || !isRetrieverDataSet),
    isDataFlowStatus,
    isFlowRunPreview,
    aclQueryMode,
    toggleUseExpanded,
    splitterNodeProps,
    isNodeDiscoveryEnabled,
    canvasScheduleControlsActive,
    isMultifileUploadEnabledOnSource,
  };

  switch (item.type) {
    case 'SOURCE':
      if (node_type === 'data_retriever_holder' && isEdit) {
        return (
          <DataSourceCardContainer
            {...cardProps}
            ActionComponent={RetrieverActions}
            onItemClick={onItemClick}
          />
        );
      }

      return (
        <DataSourceCardContainer
          {...cardProps}
          hideActions={node_type === 'data_retriever_holder'}
          onItemClick={onItemClick}
        />
      );

    case 'DATA_SINK':
      return <DataSinkCardContainer {...cardProps} onItemClick={onItemClick} />;

    case 'DATA_SET':
      if (node_type === 'data_retriever_holder' && isEdit) {
        // root data set node
        if (isRetrieverDataSet) {
          return (
            <div>
              <DataSetCardContainer
                {...cardProps}
                ActionComponent={RetrieverActions}
                hideNexsetActions
                onItemClick={onItemClick}
              />
              {!cardProps.data.children?.length && (
                <ActionMenu>
                  <ModalConfigureReranker
                    ButtonComponent={<ActionItem onClick={onAddReranker}>Add Reranker</ActionItem>}
                    {...cardProps}
                    parentNexset={cardProps.data}
                  />
                </ActionMenu>
              )}
            </div>
          );
        }

        // leaf data set node
        if (!cardProps.data.children?.length) {
          return (
            <div>
              <DataSetCardContainer {...cardProps} hideActions onItemClick={onItemClick} />
              <ActionMenu>
                <ModalConfigureReranker
                  ButtonComponent={<ActionItem onClick={onAddReranker}>Add Reranker</ActionItem>}
                  {...cardProps}
                  parentNexset={cardProps.data}
                />
              </ActionMenu>
            </div>
          );
        }
      } else if (item.node_type === 'splitter') {
        return (
          <NodeBox
            data={item}
            className={classes.iconContainer}
            variant="iconOnly"
            shape="bulletInverted"
            icon={<SplitIcon />}
            onClick={splitterNodeProps?.onEditButtonClicked}
          >
            <ResourceName {...cardProps} {...splitterNodeProps} isCollapsed={true}>
              Splitter
            </ResourceName>
          </NodeBox>
        );
      }

      return (
        <DataSetCardContainer
          {...cardProps}
          hideActions={node_type === 'data_retriever_holder'}
          onItemClick={onItemClick}
        />
      );

    default:
      return (
        <ResourceCard
          {...cardProps}
          data={{
            ...item,
            resourceType: item.type,
          }}
          shouldUseResourceIcon
          resourceType={item.type}
          size="small"
          status={item.status}
        />
      );
  }
}

function ResourceNode(props: any) {
  const { data } = props;
  const {
    nodeKey,
    selectedItem,
    isRoot,
    expanded,
    expandedNodes,
    parentId,
    flow,
    item,
    branches,
    longestDataSetChainLength,
    bottomHandleStatus,
    topHandleStatus,
    isPreview,
    isSecondaryFlow,
    layoutDirection = 'LR',
    byoV2Enabled,
    highlightedNodeKey,
  } = data;
  const ragFlowId = useRagFlowId();
  const { key, ...restData } = data;
  const canUseScheduleCanvasNodes = !!data?.showScheduleCanvasHandles;
  const isSourceLikeCard =
    item.type === 'SOURCE' ||
    item.type === 'data_source' ||
    (item.node_type === 'data_source' && item.type !== 'DATA_SET' && item.type !== 'DATA_SINK');

  const sourcePosition =
    layoutDirection === 'TB'
      ? Position.Bottom
      : layoutDirection === 'RL'
        ? Position.Left
        : Position.Right;
  const targetPosition =
    layoutDirection === 'TB'
      ? Position.Top
      : layoutDirection === 'RL'
        ? Position.Right
        : Position.Left;
  const parentDataSet = useSelector(
    (state: any) => state.entities.dataSets[item?.parent_data_set_id],
  );
  const vendors = useSelector((state: any) => state.entities.vendors);
  const metaInfoById = useSelector((state: any) => state.paging.metaInfoById);
  const [isSplitDialogOpen, setIsSplitDialogOpen] = useState(false);

  if (!item) return <div />;

  let children: any;
  if (has(item, 'children') && item.children.length) {
    children = item.children;
  }

  // BYO (custom) flows render a synthetic Code node off the source even before
  // a sink exists, so the source always needs its right (source) handle for the
  // source → code edge — not just when it has children.
  const isByoSource =
    !!byoV2Enabled &&
    item.type === 'SOURCE' &&
    (item.flow_type === FLOW_TYPE_CUSTOM || flow?.flow_type === FLOW_TYPE_CUSTOM);
  let isBranchSelected = false;
  let isOtherBranchSelected = false;

  if (selectedItem && branches[selectedItem]) {
    isBranchSelected =
      branches[nodeKey].items.indexOf(selectedItem) > -1 ||
      branches[selectedItem].items.indexOf(nodeKey) > -1;
    isOtherBranchSelected = !isBranchSelected;
  }

  const pipeItemStyle: any = {};

  const triggers: any[] = flow?.flow_triggers || item?.flow_triggers;
  const isSourceTriggered = triggers && item.type === 'SOURCE';
  const isSourceTrigger = triggers?.some((trigger) => trigger.id === item.id);

  // TODO: consider moving this condition to mappers and getting rid of
  // motion.div completely
  const hide = data.isSecondaryFlow && parentId && !expandedNodes.includes(parentId);

  if (item.type === 'DATA_SINK') {
    pipeItemStyle.marginLeft =
      max([longestDataSetChainLength - item.depth + 1, 0]) *
        (PIPELINE_CARD_WIDTH + PIPELINE_CARD_SPACING) +
      20;
  }

  let connectionCategory = null;

  if (item.type === 'SOURCE') {
    const connectionOrVendorType = getConnectionOrVendorType(
      item,
      vendors,
      'source_type',
      'vendor_id',
    );
    connectionCategory = isSqlAutomation(item, vendors)
      ? metaInfoById['sql_runner']?.connectionCategory
      : metaInfoById[connectionOrVendorType]?.connectionCategory;
  }

  const onMouseEnter = () => {
    const { nodeKey, selectedItem, select } = props;

    if (nodeKey !== selectedItem) {
      select(nodeKey);
    }
  };

  const onCloseSplitDialog = () => {
    setIsSplitDialogOpen(false);
  };

  const onOpenSplitDialog = () => {
    setIsSplitDialogOpen(true);
  };

  return (
    <AnimatePresence>
      {!hide && (
        <motion.div
          exit={isSecondaryFlow ? { opacity: 0 } : undefined}
          initial={isSecondaryFlow ? { opacity: 0 } : undefined}
          animate={isSecondaryFlow ? { opacity: 1, transition: { delay: 0.2 } } : undefined}
          key={item.nodeKey}
          style={styles.main}
          data-id={item.id}
          data-testid={`${item.type}-card`}
        >
          <div
            className={classnames(`pipe-item-${nodeKey}`, 'pipe-item', {
              'pipe-item-selected': isBranchSelected,
              'pipe-item-is-root': isRoot,
              'other-pipe-item-selected': isOtherBranchSelected,
              [classes.highlightedNode]:
                highlightedNodeKey != null && String(highlightedNodeKey) === String(nodeKey),
            })}
            style={pipeItemStyle}
            onMouseEnter={onMouseEnter}
          >
            <div style={{ position: 'relative', width: '100%' }}>
              <ItemCard
                {...restData}
                expanded={expanded}
                item={item}
                isPreview={isPreview}
                isNodeDiscoveryEnabled={Boolean(isPreview && data?.onItemClick)}
                canvasScheduleControlsActive={canUseScheduleCanvasNodes}
                splitterNodeProps={
                  item.node_type === 'splitter'
                    ? {
                        showTransform: false,
                        showSend: false,
                        onEditButtonClicked: onOpenSplitDialog,
                      }
                    : {}
                }
                isMultifileUploadEnabledOnSource={
                  item.type === 'SOURCE' && connectionCategory === CONNECTION_CATEGORY_FILE_UPLOAD
                }
              />
            </div>
            {(children || isByoSource) && (
              <Handle
                isConnectable={false}
                className={classes.handle}
                type="source"
                position={sourcePosition}
                id="right"
              />
            )}
            {!!item.parent_node_id && (
              <Handle
                isConnectable={false}
                className={classes.handle_left}
                type="target"
                position={targetPosition}
                id="left"
              />
            )}
            {!!topHandleStatus ? (
              <Handle
                isConnectable={false}
                className={classnames(classes.handle, classes['handle_' + topHandleStatus])}
                type="target"
                position={Position.Top}
                id="top"
              />
            ) : null}
            {!!bottomHandleStatus ? (
              <Handle
                isConnectable={false}
                className={classnames(classes.handle, classes['handle_' + bottomHandleStatus])}
                type="source"
                position={Position.Bottom}
                id="bottom"
              />
            ) : null}

            {isSourceLikeCard && canUseScheduleCanvasNodes && (
              <Handle
                isConnectable={false}
                className={classes.handle}
                type="target"
                position={layoutDirection === 'TB' ? Position.Top : Position.Left}
                id="flow-control-in"
              />
            )}
            {isSourceLikeCard && (item.data_credentials_id || item.data_credentials_group_id) && (
              <Handle
                isConnectable={false}
                className={classes.handle}
                type="target"
                position={layoutDirection === 'TB' ? Position.Left : Position.Bottom}
                id="credential-in"
              />
            )}

            <Handle
              isConnectable={false}
              className={classnames(classes.handle, classes['handle_DEPENDENCY'])}
              type={'target'}
              position={Position.Bottom}
              id="bottom-dependency"
            />

            <Handle
              isConnectable={false}
              className={classnames(classes.handle, classes['handle_DEPENDENCY'])}
              type={isSourceTriggered ? 'target' : 'source'}
              position={Position.Bottom}
              id="bottom-dependency"
            />
          </div>
          {isSplitDialogOpen && (
            <DataSetSplitDialog
              parentDataSet={parentDataSet}
              splitterDataSet={item}
              onClose={onCloseSplitDialog}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
export default memo(ResourceNode, (prev, next) => {
  const keys = [
    'nodeKey',
    'selectedItem',
    'highlightedNodeKey',
    'isRoot',
    'expanded',
    'expandedNodes',
    'parentId',
    'flow',
    'item',
    'branches',
    'longestDataSetChainLength',
    'bottomHandleStatus',
    'topHandleStatus',
    'isPreview',
    'isFlowRunPreview',
    'layoutDirection',
    'showScheduleCanvasHandles',
  ];

  return !keys.some((key) => prev?.data?.[key] !== next?.data?.[key]);
});
